import { addDays } from "date-fns";
import { getManualDocumentCountryValue } from "src/pages/ManualDocumentV2/utils/country";
import { getTaxRate } from "src/utils/manualDoc";
import type { TDocumentMapper, TUserProfileMapper } from "../types";

export const defaultVendorProfileMapper: TUserProfileMapper = (data) => ({
  vendorCompanyName: data.organizationName ?? "",
  // Force the user to explicitly pick (or type) a vendor company ID instead
  // of silently inheriting the org's registrationId. The raw value is kept
  // on _vendorCompanyIDSource so the dropdown can still surface it as an
  // option (including comma-separated multi-ID lists).
  vendorCompanyID: "",
  _vendorCompanyIDSource: data.registrationId ?? "",
  vendorEmail: data.email ?? "",
  vendorCompanyStreet: data?.addressDetail?.street ?? "",
  vendorCompanyStreetNumber: data?.addressDetail?.streetNumber ?? "",
  vendorCompanyLocality: data?.addressDetail?.municipality ?? "",
  vendorCompanyRegion: data?.addressDetail?.province ?? "",
  vendorCompanyZipCode: data?.addressDetail?.zipCode ?? "",
  vendorCountry: getManualDocumentCountryValue(data.organizationCountry ?? ""),
  logoURL: data?.companyLogoURL ?? "",
  documentDate: new Date(),
  dueDate: addDays(new Date(), 30),
  vendorPeppolInboxCountry:
    data.peppolInboxCountry ?? data.vendorPeppolInboxCountry ?? "",
  vendorPeppolSchemeId: data.peppolSchemeId ?? data.vendorPeppolSchemeId ?? "",
  vendorPeppolParticipantId:
    data.peppolParticipantId ?? data.vendorPeppolParticipantId ?? "",
});

export const defaultCustomerProfileMapper: TUserProfileMapper = (data) => ({
  customerCompanyName: data.organizationName ?? "",
  customerCompanyID: "",
  _customerCompanyIDSource: data.registrationId ?? "",
  customerEmail: data.email ?? "",
  customerCompanyAddress: data.companyAddress ?? "",
  customerCompanyStreet: data?.addressDetail?.street ?? "",
  customerCompanyStreetNumber: data?.addressDetail?.streetNumber ?? "",
  customerCompanyLocality: data?.addressDetail?.municipality ?? "",
  customerCompanyRegion: data?.addressDetail?.province ?? "",
  customerCompanyZipCode: data?.addressDetail?.zipCode ?? "",
  customerCountry: getManualDocumentCountryValue(
    data.organizationCountry ?? "",
  ),
  logoURL: data?.companyLogoURL ?? "",
  documentDate: new Date(),
  dueDate: addDays(new Date(), 30),
  customerPeppolSchemeId:
    data.peppolSchemeId ?? data.customerPeppolSchemeId ?? "",
  customerPeppolParticipantId:
    data.peppolParticipantId ?? data.customerPeppolParticipantId ?? "",
});

export const defaultDocumentMapper: TDocumentMapper = (
  data,
  { isDuplicate, taxRates },
) => ({
  documentDate: isDuplicate ? new Date() : data.DocumentDate ?? null,
  dueDate: isDuplicate ? addDays(new Date(), 30) : data.DueDate ?? null,
  poNumber: data?.PONumber ?? "",
  series: isDuplicate ? undefined : data.Series,
  deliveryNote: data?.DeliveryNote ?? "",
  paymentTerms: data.PaymentTerms ?? "",
  customerCompanyName: data.CustomerCompanyName ?? "",
  customerCompanyID: data.CustomerCompanyID ?? "",
  customerCompanyStreet: data.CustomerCompanyStreet ?? "",
  customerCompanyStreetNumber: data.CustomerCompanyStreetNumber ?? "",
  customerCompanyLocality: data.CustomerCompanyLocality ?? "",
  customerCompanyRegion: data.CustomerCompanyRegion ?? "",
  customerCompanyZipCode: data.CustomerCompanyZipCode ?? "",
  customerCountry: getManualDocumentCountryValue(data.CustomerCountry),
  customerCompanyAddress: data.CustomerCompanyAddress ?? "",
  customerEmail: data.CustomerEmail ?? "",
  customerPeppolSchemeId: data.CustomerPeppolSchemeId ?? "",
  customerPeppolParticipantId: data.CustomerPeppolParticipantId ?? "",

  vendorCompanyName: data.VendorCompanyName ?? "",
  vendorCompanyID: data.VendorCompanyID ?? "",
  vendorCompanyStreet: data.VendorCompanyStreet ?? "",
  vendorCompanyStreetNumber: data.VendorCompanyStreetNumber ?? "",
  vendorCompanyLocality: data.VendorCompanyLocality ?? "",
  vendorCompanyRegion: data.VendorCompanyRegion ?? "",
  vendorCompanyZipCode: data.VendorCompanyZipCode ?? "",
  vendorCountry: getManualDocumentCountryValue(data.VendorCountry),
  vendorCompanyAddress: data.VendorCompanyAddress ?? "",
  vendorEmail: data.VendorEmail ?? "",
  vendorPeppolInboxCountry: data.VendorPeppolInboxCountry ?? "",
  vendorPeppolSchemeId: data.VendorPeppolSchemeId ?? "",
  vendorPeppolParticipantId: data.VendorPeppolParticipantId ?? "",

  logoURL: data.LogoURL ?? "",
  totalWithoutTax: data.TotalwithoutTax ? Number(data.TotalwithoutTax) : null,
  totalDiscount: data.TotalDiscount ? Number(data.TotalDiscount) : null,
  currency: data.Currency ?? "",
  items:
    data.Items?.map((item: any) => ({
      referenceCode: item.ReferenceCode ?? "",
      description: item.Description ?? "",
      unitPrice: item.UnitPrice ? Number(item.UnitPrice) : null,
      quantity: item.Quantity ? Number(item.Quantity) : null,
      vat: item.VAT ? Number(item.VAT) : null,
      dto: item.Dto ? Number(item.Dto) : null,
      deliveryNote: item.DeliveryNote ?? "",
      poNumber: item.PoNumber ?? "",
      taxId: item?.TaxId ?? "",
      taxType:
        getTaxRate({ taxRates, taxId: item?.TaxId })?.taxType ?? item.TaxType,
      vatPercentage:
        getTaxRate({ taxRates, taxId: item?.TaxId })?.rate ??
        (item.VATPercentage ? Number(item.VATPercentage) : null),
      isWithHoldingTax: getTaxRate({ taxRates, taxId: item?.TaxId })
        ?.isWithholdingTax,
    })) ?? [],
  taxes:
    data.Taxes?.map((tax: any) => ({
      baseAmount: tax.BaseAmount ? Number(tax.BaseAmount) : null,
      vat: tax.VAT ? Number(tax.VAT) : null,
      taxId: tax?.TaxId ?? "",
      taxType:
        getTaxRate({ taxRates, taxId: tax?.TaxId })?.taxType ?? tax.TaxType,
      vatPercentage:
        getTaxRate({ taxRates, taxId: tax?.TaxId })?.rate ??
        (tax.VATPercentage ? Number(tax.VATPercentage) : null),
      isWithHoldingTax: getTaxRate({ taxRates, taxId: tax?.TaxId })
        ?.isWithholdingTax,
    })) ?? [],
  uploadedDocument: data?.ReferenceDocumentURL,
  comments: data?.Comments ?? "",

  RecurringDocument: {
    startDate: data?.RecurringDocument?.StartDate ?? null,
    endDate: data?.RecurringDocument?.EndDate ?? null,
    RecurringFrequencyType:
      data?.RecurringDocument?.RecurringFrequencyType ?? "",
    InvoiceDueInDays: data?.RecurringDocument?.InvoiceDueInDays ?? null,
  },
});
